import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { AdminMenu } from './components/admin-menu.tsx'
import { AdminSidebar } from './components/admin-sidebar.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { SearchBar } from './components/search-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import { LocaleSwitch } from './routes/resources/locale-switch.tsx'
import {
	ThemeSwitch,
	useOptionalTheme,
	useTheme,
} from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import {
	getAppDescription,
	getAppName,
	getAppNameLines,
} from './utils/branding.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { getLocale } from './utils/i18n.server.ts'
import { useTranslation } from './utils/i18n.ts'
import { cn, combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser, userHasRoleAtLeast } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	const appName = getAppName()
	return [
		{
			title: data ? appName : `Error | ${appName}`,
		},
		{ name: 'description', content: getAppDescription() },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				locale: getLocale(request),
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	lang = 'uk',
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
	lang?: string
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang={lang} className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document
			nonce={nonce}
			theme={theme}
			// loader error → data is null; fall back to the server's own ENV so a
			// rebranded instance never hydrates the shipped default over its brand
			// (a public key must sit in BOTH branding.ts and the getEnv() whitelist —
			// miss the whitelist and only post-hydration goes wrong)
			env={data?.ENV ?? ENV}
			lang={data?.requestInfo.locale ?? 'uk'}
		>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const matches = useMatches()
	const isOnSearchPage = matches.find((m) => m.id === 'routes/users/index')
	// the same predicate gates the admin shell (sidebar / «Меню») and the global
	// search — no new authorization concept, one expression to reason about
	const isAdmin = userHasRoleAtLeast(user, 'admin')
	// The global search leads to the admin-only /users surface; showing it to
	// anon / non-admin is a broken affordance (they'd only hit /login or 403).
	const canSearch = isAdmin
	const searchBar =
		isOnSearchPage || !canSearch ? null : <SearchBar status="idle" />
	const t = useTranslation()
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="flex min-h-screen flex-col justify-between">
				<header className="container py-6">
					{/* the nav stays wrappable until `lg` — the SAME breakpoint the two
					    search slots key off. Below `lg` the full-width row at the end of
					    this nav is the visible search, and it can only take its own row
					    if wrapping is still on; `sm:flex-nowrap` (the earlier value) let
					    it get squeezed into the first row for the whole sm→lg band. At
					    `lg` and up that row is `hidden` and the inline slot takes over,
					    so nowrap is safe from exactly there. */}
					<nav className="flex flex-wrap items-center justify-between gap-4 md:gap-8 lg:flex-nowrap">
						<Logo />
						{/* both search slots key off ONE breakpoint (lg) — see the
						    full-width row below: any mismatch between the two would leave
						    a width band with no search at all.

						    Width is a floor-and-cap band, not two fixed steps: the earlier
						    `w-48 xl:w-64` pair made the slot jump 192↔256px the moment a
						    zoom change crossed `xl`. `min-w-48` is the anti-collapse floor
						    the fixed `w-48` used to provide — with a bare `flex-1`
						    (flex-basis 0) this slot was the first child to collapse when
						    the nav overflowed, and the floor is what stops that; `max-w-64`
						    keeps it from swallowing the row on a wide screen.

						    Measured, so the band is not read as live tuning: at today's
						    header content the slot sits at its 256px cap at EVERY viewport
						    ≥1024 — the row always has spare space. The floor only engages
						    once the row grows enough to crowd it; with `flex-basis: 0` the
						    slot then freezes at 192px and the rest of the row absorbs the
						    overflow — exactly the guarantee `shrink-0` used to give. */}
						<div className="ml-auto hidden max-w-64 min-w-48 flex-1 lg:block">
							{searchBar}
						</div>
						{/* The admin row carries one control more than anybody else's (the
						    «Меню» button), and at 375px `gap-10` twice over plus the
						    user's full name ran the group past the viewport — silently,
						    because `html` is `overflow-x-hidden`. Only the admin case
						    tightens: the anon / non-admin group keeps `gap-10` exactly,
						    so their markup is untouched by this change. */}
						<div
							className={cn(
								'flex items-center',
								isAdmin ? 'gap-4 sm:gap-6 lg:gap-10' : 'gap-10',
							)}
						>
							{user ? (
								<>
									<Link to="/cabinet" className="text-sm font-medium">
										{t('nav.cabinet')}
									</Link>
									{/* the eleven admin destinations moved OUT of this row — they
									    live in the `lg`+ sidebar, and below `lg` behind this
									    button. `/cabinet` stays: it is every logged-in user's
									    link, not an admin one. */}
									{isAdmin ? <AdminMenu className="lg:hidden" /> : null}
									{/* below `sm` an admin's name is the widest droppable thing
									    in the row; it stays in the control's aria-label */}
									<UserDropdown hideNameBelowSm={isAdmin} />
								</>
							) : (
								<Button asChild variant="default" size="lg">
									<Link to="/login">{t('nav.login')}</Link>
								</Button>
							)}
						</div>
						<div className="block w-full lg:hidden">{searchBar}</div>
					</nav>
				</header>

				{isAdmin ? (
					<div className="flex flex-1">
						<AdminSidebar />
						{/* min-w-0: a flex item defaults to min-width:auto, so a wide table
						    would push the row past the viewport instead of staying inside
						    its column.

						    The right inset MIRRORS the sidebar's left one (decisions #129):
						    this row is full-bleed, so without it the content column ran to
						    the viewport edge while the header, the footer and the sidebar
						    all stopped at the 1400px container.

						    It carries the centring offset ONLY — no `+2rem`, unlike the
						    sidebar's. Route mains are `container` themselves and bring that
						    2rem with them; adding it here as well would leave 4rem of dead
						    inset. Above the cap the column is then a constant
						    1400 − 2rem − 14rem (the sidebar) = 1144px, and the main inside
						    it lines its RIGHT edge up with the header's — box on box,
						    content on content, since the two share the same padding (the
						    left edge belongs to the sidebar).

						    `100%` against the sidebar's `100vw`: a percentage resolves
						    against this row's width — the LAYOUT viewport, which is what
						    the header container is laid out in too. `100vw` also counts a
						    classic scrollbar, and that difference IS the ~7-8px drift
						    docs/ui-smoke.md item 15 records for the sidebar; the right edge
						    has no reason to inherit it. Reconciling the sidebar to `100%`
						    is a separate change and unmeasurable on the current smoke
						    instrument (headless Chromium draws overlay scrollbars). */}
						<div className="flex min-w-0 flex-1 flex-col min-[1400px]:pe-[calc((100%-1400px)/2)]">
							<Outlet />
						</div>
					</div>
				) : (
					// anon and non-admin keep the pre-sidebar shell exactly — the branch
					// is duplicated on purpose so their markup is untouched by this change
					<div className="flex flex-1 flex-col">
						<Outlet />
					</div>
				)}

				<div className="container flex justify-between pb-5">
					<Logo />
					<div className="flex items-center gap-2">
						<LocaleSwitch />
						<ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
					</div>
				</div>
			</div>
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function Logo() {
	// Робоча назва продукту (decisions #9, team-confirmed батч 1) — дефолт живе
	// в app/utils/branding.ts, інстанс перекриває через APP_NAME. Бренд НЕ
	// перекладається — той самий рядок в обох локалях; aria-label дає стабільне
	// ім'я лінка (двоспановий спліт лишає візуал).
	const appName = getAppName()
	const { first, rest } = getAppNameLines()
	return (
		<Link to="/" className="group grid leading-snug" aria-label={appName}>
			<span className="font-light transition group-hover:-translate-x-1">
				{first}
			</span>
			{rest ? (
				<span className="font-bold transition group-hover:translate-x-1">
					{rest}
				</span>
			) : null}
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
